import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Eye, EyeOff, MicOff, Phone, Sparkles, Target, ThumbsUp, TrendingUp, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDuration, fmtPhone, fmtShort, num, pct } from "@/lib/format";
import { Avatar, EmptyState, KpiCard, Loading, PageHeader, Pill, RangePicker, ScoreBadge, Section, severityTone, useRange } from "@/components/common";
import CallDrawer from "@/components/CallDrawer";
import { ScoreHistory, DimensionCompare } from "@/components/scorecard/ScoreHistory";
import { CoachingTimeline } from "@/components/scorecard/CoachingTimeline";
import { CoachingThemes } from "@/components/scorecard/CoachingThemes";

function CallList({ title, eyebrow, calls, tone, onOpen, testId, empty }) {
  return (
    <Section title={title} eyebrow={eyebrow} padded={false} testId={testId}>
      {calls.length ? (
        <ul className="divide-y divide-[#1f293d]">
          {calls.map((c) => (
            <li key={c.id}>
              <button data-testid={`${testId}-call-${c.id}`} onClick={() => onOpen(c.id)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left row-hover">
                <ScoreBadge score={c.score} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-100 truncate">{c.topic || "Customer call"}</div>
                  <div className="text-[11px] font-mono text-slate-500">{fmtShort(c.created_at)} · {fmtPhone(c.client_number)} · {fmtDuration(c.duration)}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      ) : <EmptyState icon={tone === "emerald" ? ThumbsUp : Target} title={empty} />}
    </Section>
  );
}

export default function AgentScorecard() {
  const { id } = useParams();
  const [range, setRange] = useRange(30);
  const [params, setParams] = useSearchParams();
  const openId = params.get("call");
  const rangeParams = { date_from: range.from, date_to: range.to };
  const q = useQuery({ queryKey: ["scorecard", id, rangeParams], queryFn: () => api.get(`/agents/${id}/scorecard`, { params: rangeParams }).then((r) => r.data), retry: (n, err) => err?.response?.status !== 404 && n < 2 });
  const open = (callId) => setParams({ call: callId });

  if (q.isLoading) return <Loading label="Building scorecard" />;
  if (q.isError || !q.data) return <Section><EmptyState title="Agent not found" hint="This OpenPhone user is no longer in the roster." action={<Link to="/agents" className="text-xs text-blue-400">← Back to agents</Link>} testId="scorecard-not-found" /></Section>;

  const d = q.data;
  const a = d.agent;
  const s = d.stats || {};
  const p = d.previous || {};
  const delta = (k) => (s[k] != null && p[k] != null ? s[k] - p[k] : undefined);

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/agents" className="inline-flex items-center gap-1 hover:text-blue-300" data-testid="scorecard-back-link"><ArrowLeft className="h-3 w-3" /> Agent performance</Link>}
        title={a.name}
        description="Score history, coaching timeline, recurring themes and compliance for this team member."
        actions={<RangePicker range={range} onChange={setRange} />}
        testId="scorecard-header"
      />

      <div className="panel p-4 flex flex-wrap items-center gap-4 fade-up" data-testid="scorecard-identity">
        <Avatar name={a.name} url={a.picture_url} size="md" />
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-100">{a.name}</div>
          <div className="text-xs font-mono text-slate-500 truncate">{a.email || "no email on OpenPhone"}{a.slack_user_id ? ` · Slack ${a.slack_user_id}` : ""}</div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {a.role === "owner" && <Pill tone="violet">owner</Pill>}
          {a.monitored ? <Pill tone="violet"><Eye className="h-3 w-3" /> AI monitored</Pill> : <Pill><EyeOff className="h-3 w-3" /> not monitored</Pill>}
          {s.feedback_sent > 0 && <Pill tone="blue">{s.feedback_sent} coaching emails</Pill>}
        </div>
        <Link to={`/calls?agent=${id}`} className="ml-auto text-xs text-blue-400 hover:text-blue-300" data-testid="scorecard-all-calls-link">All calls →</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="scorecard-kpis">
        <KpiCard testId="sc-kpi-calls" label="Calls" value={num(s.calls)} delta={delta("calls")} icon={Phone} sub={`${num(s.inbound)} in · ${num(s.outbound)} out`} />
        <KpiCard testId="sc-kpi-reviewed" label="AI reviewed" value={num(s.analyzed)} delta={delta("analyzed")} icon={Sparkles} tone="violet" sub={`${fmtDuration(s.talk_seconds)} talk time`} />
        <KpiCard testId="sc-kpi-score" label="Avg score" value={s.avg_score ?? "—"} delta={s.avg_score != null && p.avg_score != null ? s.avg_score - p.avg_score : undefined} icon={Target} tone="emerald" sub={d.team.avg_score != null ? `team ${d.team.avg_score}` : `threshold ${d.threshold}`} />
        <KpiCard testId="sc-kpi-below" label="Below threshold" value={num(s.below_threshold)} delta={delta("below_threshold")} invert icon={TrendingUp} tone="red" sub={`${pct(s.below_threshold, s.analyzed)}% of reviewed`} />
        <KpiCard testId="sc-kpi-positive" label="Positive calls" value={s.analyzed ? `${pct(s.positive, s.analyzed)}%` : "—"} icon={ThumbsUp} tone="cyan" sub={`${num(s.negative)} negative`} />
        <KpiCard testId="sc-kpi-unrecorded" label="Not recorded" value={num(s.unrecorded)} icon={MicOff} tone={s.unrecorded ? "red" : "slate"} sub="calls ≥ threshold" />
        <KpiCard testId="sc-kpi-sms" label="SMS SLA alerts" value={num(s.sms_alerts)} delta={delta("sms_alerts")} invert icon={Zap} tone="amber" sub="late text replies" />
        <KpiCard testId="sc-kpi-anomalies" label="Open anomalies" value={num(s.anomalies)} icon={AlertTriangle} tone={s.anomalies ? "red" : "slate"} sub="patterns flagged" />
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        <ScoreHistory series={d.series} threshold={d.threshold} className="xl:col-span-2" />
        <DimensionCompare stats={s} team={d.team} />
      </div>

      <div className="grid xl:grid-cols-3 gap-4 items-start">
        <CoachingTimeline items={d.timeline} threshold={d.threshold} onOpen={open} className="xl:col-span-2" />
        <div className="space-y-4">
          <CoachingThemes agentId={id} rangeParams={rangeParams} analyzed={s.analyzed || 0} />
          <CallList title="Best calls" eyebrow="Top scores" calls={d.best} tone="emerald" onOpen={open} testId="scorecard-best" empty="No scored calls yet" />
          <CallList title="Needs coaching" eyebrow="Lowest scores" calls={d.worst} tone="red" onOpen={open} testId="scorecard-worst" empty="No scored calls yet" />
          <Section title="Unrecorded calls" eyebrow="Recording compliance" padded={false} testId="scorecard-unrecorded">
            {d.unrecorded.length ? (
              <ul className="divide-y divide-[#1f293d]">
                {d.unrecorded.map((c) => (
                  <li key={c.id}><button data-testid={`scorecard-unrecorded-${c.id}`} onClick={() => open(c.id)} className="w-full flex items-center gap-2 px-4 py-2 text-xs text-left row-hover"><MicOff className="h-3 w-3 text-red-400" /><span className="font-mono text-slate-500">{fmtShort(c.created_at)}</span><span className="font-mono text-slate-300 ml-auto">{fmtPhone(c.client_number)} · {fmtDuration(c.duration)}</span></button></li>
                ))}
              </ul>
            ) : <EmptyState icon={MicOff} title="Every qualifying call was recorded" />}
          </Section>
          <Section title="Anomalies" eyebrow="Unusual activity" padded={false} testId="scorecard-anomalies">
            {d.anomalies.length ? (
              <ul className="divide-y divide-[#1f293d]">
                {d.anomalies.map((an) => (
                  <li key={an.id} className="px-4 py-2 flex items-start gap-2 text-xs"><Pill tone={severityTone(an.severity)}>{an.severity}</Pill><span className="text-slate-200 flex-1">{an.title}</span><Pill tone={an.status === "open" ? "amber" : "slate"}>{an.status}</Pill></li>
                ))}
              </ul>
            ) : <EmptyState icon={AlertTriangle} title="Nothing unusual" />}
          </Section>
        </div>
      </div>

      <CallDrawer callId={openId} onClose={() => setParams({})} />
    </>
  );
}
